import React, { useState } from "react";
import GoogleTranslateWidget from "./GoogleTranslateWidget";

/** Exact copy from v0 check-in reference (disabled-consent hint). */
export const V0_CONSENT_DISABLED_HINT =
  "Please upload a valid ID and a selfie first, then you can accept the agreement.";

/**
 * Legacy rental summary body from v0 `contract-consent.tsx`.
 * Kept as a fallback/helper for tests and older call sites.
 */
export const V0_RENTAL_AGREEMENT_SUMMARY_LINES = [
  "Check-in time: 15:00 — Check-out time: 11:00. No smoking on the premises. Maximum occupancy as stated in booking. Guests are responsible for any damage to the property during their stay. Quiet hours: 22:00–08:00.",
];

const RENTAL_AGREEMENT_POLICY_COPY =
  "No smoking on the premises. Maximum occupancy as stated in booking. Guests are responsible for any damage to the property during their stay. Quiet hours: 22:00–08:00.";

export const formatReservationTimeInTimezone = (dateValue, timezone) => {
  if (!dateValue || !timezone) return null;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;

  try {
    return new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: timezone,
    }).format(date);
  } catch (error) {
    return null;
  }
};

export const buildRentalAgreementSummaryLines = (reservationInfo) => {
  const timezone = reservationInfo?.listing?.timezone;
  const checkInTime = formatReservationTimeInTimezone(reservationInfo?.checkIn, timezone);
  const checkOutTime = formatReservationTimeInTimezone(reservationInfo?.checkOut, timezone);

  if (!checkInTime || !checkOutTime) {
    return V0_RENTAL_AGREEMENT_SUMMARY_LINES;
  }

  return [
    `Check-in time: ${checkInTime} — Check-out time: ${checkOutTime}. ${RENTAL_AGREEMENT_POLICY_COPY}`,
  ];
};

const patchTimeAfterLabel = (line, labelPattern, time) =>
  line.replace(
    new RegExp(`(${labelPattern}[^0-9]{0,40})(\\d{1,2}[:.]\\d{2})`, "i"),
    (_match, prefix) => `${prefix}${time}`
  );

export const patchAgreementTimes = (agreementLines, reservationInfo) => {
  const timezone = reservationInfo?.listing?.timezone;
  const checkInTime = formatReservationTimeInTimezone(reservationInfo?.checkIn, timezone);
  const checkOutTime = formatReservationTimeInTimezone(reservationInfo?.checkOut, timezone);

  if (!checkInTime || !checkOutTime || !Array.isArray(agreementLines)) {
    return agreementLines;
  }

  return agreementLines.map((line) => {
    if (typeof line !== "string" || !line) return line;

    let patched = patchTimeAfterLabel(line, "check[-\\s]?in", checkInTime);
    patched = patchTimeAfterLabel(patched, "check[-\\s]?out", checkOutTime);
    return patched;
  });
};

export function Icon({ name, size = 24 }) {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
  };

  if (name === "lock") {
    return (
      <svg {...common}>
        <rect x="4" y="10" width="16" height="10" rx="2" />
        <path d="M8 10V7a4 4 0 0 1 8 0v3" />
      </svg>
    );
  }

  if (name === "check") {
    return (
      <svg {...common}>
        <path d="M20 6 9 17l-5-5" />
      </svg>
    );
  }

  if (name === "shield") {
    return (
      <svg {...common}>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
        <path d="m9 12 2 2 4-4" />
      </svg>
    );
  }

  if (name === "info") {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="10" />
        <path d="M12 11v5" />
        <path d="M12 8h.01" />
      </svg>
    );
  }

  if (name === "alert") {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="10" />
        <path d="M12 8v4" />
        <path d="M12 16h.01" />
      </svg>
    );
  }

  return (
    <svg {...common}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="m17 8-5-5-5 5" />
      <path d="M12 3v12" />
    </svg>
  );
}

export function CheckInShell({ children }) {
  return (
    <main className="v0-shell">
      <TopBar />
      <div className="v0-main-card">{children}</div>
    </main>
  );
}

export function TopBar() {
  return (
    <header className="v0-top-bar">
      <div className="v0-brand">
        <div className="v0-brand-mark" aria-label="Property logo">
          PR
        </div>
        <span>Property</span>
      </div>
      <div className="v0-top-actions">
        <div className="v0-secure-badge">
          <Icon name="lock" size={13} />
          <span>Secure Check-in</span>
        </div>
        <GoogleTranslateWidget placement="top" />
      </div>
    </header>
  );
}

export function SectionLabel({ children, id }) {
  return (
    <h2 id={id} className="v0-section-label">
      {children}
    </h2>
  );
}

export function UploadZoneView({
  state,
  previewUrl,
  fileName,
  message,
  mode = "document",
  rootProps = {},
  inputProps = {},
  onVerifiedReplace,
  verifiedReplaceLabel = "Choose different photo",
}) {
  const isSelfie = mode === "selfie";
  const verified = state === "verified";
  const uploading = state === "uploading";

  return (
    <div className="v0-upload-wrapper">
      <div
        {...rootProps}
        className={`dropzone v0-upload-zone ${verified ? "is-verified" : ""} ${
          uploading ? "is-uploading" : ""
        } ${isSelfie ? "v0-selfie-upload" : ""} ${rootProps.className || ""}`}
        role={rootProps.role}
        tabIndex={rootProps.tabIndex}
        aria-label={isSelfie ? "Upload your selfie" : "Tap to upload your ID document"}
      >
        {inputProps && Object.keys(inputProps).length > 0 && <input {...inputProps} />}
        {uploading ? (
          <div className="v0-upload-state">
            <span className="v0-spinner" aria-hidden="true" />
            <p className="v0-upload-title">
              {isSelfie ? "Processing photo…" : "Verifying document…"}
            </p>
          </div>
        ) : verified ? (
          <div className="v0-upload-state">
            {previewUrl ? (
              <div className="v0-preview-wrap">
                <img
                  src={previewUrl}
                  alt={isSelfie ? "Selfie preview" : "Document preview"}
                  className="v0-preview"
                />
                <span className="v0-check-bubble">
                  <Icon name="check" size={15} />
                </span>
              </div>
            ) : (
              <span className="v0-success-icon">
                <Icon name="check" size={36} />
              </span>
            )}
            <p className="v0-upload-title">
              {isSelfie ? "Selfie confirmed" : "Document verified"}
            </p>
            {fileName && <p className="selected-file v0-upload-file">{fileName}</p>}
            {isSelfie && typeof onVerifiedReplace === "function" ? (
              <div className="v0-selfie-actions">
                <button
                  type="button"
                  className="v0-ghost-button"
                  data-testid="selfie-retake"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onVerifiedReplace();
                  }}
                >
                  {verifiedReplaceLabel}
                </button>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="v0-upload-state">
            <span className="v0-upload-icon">
              <Icon name="upload" size={40} />
            </span>
            <p className="v0-upload-title">
              {isSelfie ? "Upload your selfie" : "Upload your ID document"}
            </p>
            <p className="v0-upload-copy">
              {message ||
                (isSelfie
                  ? "A clear photo of your face in good lighting (JPG, PNG, or HEIC)"
                  : "Upload an image of your ID.")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export function SelfiePlaceholder() {
  return (
    <UploadZoneView
      mode="selfie"
      state="idle"
      previewUrl={null}
      fileName={null}
      message="A clear photo of your face in good lighting"
    />
  );
}

export function InlineHint({ show, message }) {
  return (
    <div className="v0-inline-hint-wrap" role={show ? "alert" : undefined} aria-live="polite">
      <div className={`v0-inline-hint ${show ? "is-visible" : ""}`}>
        <Icon name="alert" size={15} />
        <span>{message}</span>
      </div>
    </div>
  );
}

export function ContractConsent({
  checked,
  disabled,
  onChange,
  agreementLines = [],
  agreementLoading = false,
  agreementError = null,
  consentHintShow: consentHintShowControlled,
  onDisabledConsentInteraction,
}) {
  const [consentHintInternal, setConsentHintInternal] = useState(false);

  const showConsentHint =
    consentHintShowControlled !== undefined ? consentHintShowControlled : consentHintInternal;

  const handleDisabledClick = () => {
    if (!disabled) return;
    if (onDisabledConsentInteraction) onDisabledConsentInteraction();
    else {
      setConsentHintInternal(true);
      window.setTimeout(() => setConsentHintInternal(false), 3500);
    }
  };

  return (
    <div className="v0-consent">
      <div className="v0-agreement-box">
        {agreementLoading ? (
          <p className="v0-agreement-panel-loading">Loading rental agreement…</p>
        ) : null}
        {agreementError ? (
          <p className="v0-agreement-status v0-agreement-status--error" role="alert">
            {agreementError}
          </p>
        ) : null}
        {!agreementLoading && !agreementError ? (
          <div className="v0-agreement-panel" tabIndex={0}>
            <p className="v0-agreement-summary-title">Rental Agreement Summary</p>
            {agreementLines.length === 0 ? (
              <p className="v0-agreement-body">No agreement text was returned.</p>
            ) : (
              agreementLines.map((subtext, i) => {
                if (subtext === "") return <br key={i} />;
                return (
                  <p key={i} className="v0-agreement-body">
                    {subtext}
                  </p>
                );
              })
            )}
          </div>
        ) : null}
      </div>
      <div
        className="v0-consent-row-wrap"
        onClick={handleDisabledClick}
        style={{ cursor: disabled ? "not-allowed" : "auto" }}
      >
        <label className={`v0-consent-row ${disabled ? "is-disabled" : ""}`}>
          <span className="v0-checkbox">
            <input
              className="form-check-input"
              type="checkbox"
              checked={checked}
              value={checked}
              onChange={(event) => onChange(event.target.checked)}
              disabled={disabled}
              required
              style={{ cursor: disabled ? "not-allowed" : "pointer" }}
              aria-label="I have read and accept the rental agreement"
            />
            <span className="v0-checkbox-visual">{checked && <Icon name="check" size={16} />}</span>
          </span>
          <span className="rentalAgreement v0-consent-text">
            I have read and accept the rental agreement
          </span>
        </label>
      </div>
      <InlineHint show={showConsentHint} message={V0_CONSENT_DISABLED_HINT} />
    </div>
  );
}

export function LegalNotice() {
  return (
    <section className="v0-legal-notice" aria-label="Legal notice">
      <Icon name="shield" size={15} />
      <p>
        Il trattamento dei dati personali è obbligatorio ai sensi dell&apos;art. 109 del T.U.L.P.S. (R.D. 18 giugno
        1931, n. 773) e del D.M. 7 gennaio 2013, che impone la comunicazione alle autorità di pubblica sicurezza.
        Base giuridica: art. 6(1)(c) GDPR — obbligo legale.
      </p>
    </section>
  );
}

export function GuestChips({ guests }) {
  if (!guests.length) return null;

  return (
    <div className="v0-guest-chips">
      {guests.map((guest) => (
        <span key={guest.id} className="v0-guest-chip">
          <Icon name="check" size={13} />
          {guest.name}
        </span>
      ))}
    </div>
  );
}
